import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";

export type AdmittanceProps = {
  token: string;
  encryptionKey: string;
};

type WaitRoomProps = {
  sessionId: string;
  apiBaseUrl: string;
  onAdmitted: (admittance: AdmittanceProps) => void;
  onDismissed: () => void;
  onError: (message: string) => void;
};

type WaitRoomStatus = "checking" | "waiting";

type BaseNotification = {
  eventType: string;
  sessionId: string;
  participantId: string | undefined;
  token: string | undefined;
  encryptionKey: string | undefined;
};

const ADMITTANCE_TYPE = "DonorAdmittanceNotificationDto";
const DISMISSED_TYPE = "DonorDismissedFromWaitRoomNotificationDto";
const NO_SUCH_SESSION_TYPE = "NoSuchSessionNotificationDto";
const GENERIC_ERROR_TYPE = "GenericErrorNotificationDto";

export function WaitRoom({
  sessionId,
  apiBaseUrl,
  onAdmitted,
  onDismissed,
  onError,
}: WaitRoomProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_status, setStatus] = useState<WaitRoomStatus>("checking");
  const { t } = useTranslation();

  useEffect(
    () => {
      if (!sessionId) return;

      let cancelled = false;
      let isComplete = false;
      let eventSource: EventSource | null = null;

      const closeEventSource = () => {
        if (eventSource) {
          eventSource.close();
          eventSource = null;
        }
      };

      const connectSSE = () => {
        if (cancelled || isComplete) return;

        closeEventSource();

        setStatus("waiting");

        try {
          const sseUrl = `${apiBaseUrl}/sse/workplace/sessions/${sessionId}/wait-room`;
          console.log(`Opening SSE URL ${sseUrl}`);
          eventSource = new EventSource(sseUrl);

          eventSource.onmessage = (event: MessageEvent) => {
            if (cancelled) return;

            let parsed: BaseNotification;
            try {
              parsed = JSON.parse(event.data);
            } catch (e) {
              console.warn("Failed to parse wait-room notification:", e);
              return;
            }

            const { eventType, participantId, token, encryptionKey } = parsed;

            if (eventType === NO_SUCH_SESSION_TYPE) {
              isComplete = true;
              onError(t("errors.noSuchSession", "No such session"));
              closeEventSource();
              return;
            }

            if (eventType === GENERIC_ERROR_TYPE) {
              isComplete = true;
              onError(t("errors.genericErrorExtended"));
              closeEventSource();
              return;
            }

            if (!participantId) {
              return;
            }

            if (eventType === ADMITTANCE_TYPE && token && encryptionKey) {
              isComplete = true;
              onAdmitted({ token, encryptionKey });
              closeEventSource();
            } else if (eventType === DISMISSED_TYPE) {
              isComplete = true;
              onDismissed();
              closeEventSource();
            }
          };

          eventSource.onerror = () => {
            console.error("WaitRoom SSE error");
            if (!cancelled) {
              Sentry.captureMessage("WaitRoom SSE error", "info");
            }
          };
        } catch (err) {
          console.error("WaitRoom failed to open SSE", err);
          if (!cancelled) {
            onError(t("errors.genericErrorExtended"));
          }
        }
      };

      const handleFocus = () => {
        if (cancelled || isComplete) return;
        connectSSE();
      };

      connectSSE();
      window.addEventListener("focus", handleFocus);

      return () => {
        cancelled = true;
        window.removeEventListener("focus", handleFocus);
        closeEventSource();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBaseUrl, setStatus, sessionId],
  );

  return (
    <div
      id="wait-room"
      className="bg-blue-800/85 rounded card text-center mb-6 mt-3 wait-room"
    >
      <p className="text-xl mb-2">{t("waitRoom.heading")}</p>
      <p className="text-white font-thin text-sm">{t("waitRoom.text")}</p>
    </div>
  );
}
